import React, {useState} from 'react'
import {Button, Form, Notification, toaster} from 'rsuite'
import {useResetUserPasswordMutation} from '../api'
import {useTranslation} from 'react-i18next'
import passwordValidator from 'password-validator'

export interface ResetUserPasswordPanelProps {
  userID?: string
  userName?: string
  onClose(): void
}

export function ResetUserPasswordPanel({userID, userName, onClose}: ResetUserPasswordPanelProps) {
  const [password, setPassword] = useState('')
  const [passwordErrors, setPasswordErrors] = useState<boolean | any[]>()

  async function validatePassword() {
    const schema = new passwordValidator()

    schema
      .is()
      .min(8) // Minimum length 8
      .is()
      .max(100) // Maximum length 100
      .has()
      .uppercase() // Must have uppercase letters
      .has()
      .lowercase() // Must have lowercase letters
      .has()
      .digits(1) // Must have at least 2 digits
      .has()
      .symbols(1)
      .has()
      .not()
      .spaces() // Should not have spaces

    return schema.validate(password, {list: true})
  }

  const [resetUserPassword, {loading: isUpdating, error: updateError}] =
    useResetUserPasswordMutation()

  const isDisabled = isUpdating

  const {t} = useTranslation()

  return (
    <>
      <Form fluid={true}>
        <Form.Group>
          <Form.ControlLabel>{t('userList.panels.resetPasswordFor', {userName})}</Form.ControlLabel>
          <Form.Control
            name="resetPasswordFor"
            disabled={isDisabled}
            type="password"
            placeholder={t('userList.panels.password')}
            errorMessage={updateError?.message}
            value={password}
            onChange={(value) => setPassword(value)}
          />
        </Form.Group>
      </Form>

      {Array.isArray(passwordErrors) && passwordErrors.length !== 0 && (
        <>
          <strong>{t('userList.panels.pw.passwordRequirements')}</strong>
          {passwordErrors.map((item) => (
            <>
              <div>{t(`userList.panels.pw.${item}`)}</div>
            </>
          ))}
          <br />
        </>
      )}

      <Button
        disabled={isDisabled}
        appearance="primary"
        onClick={async () => {
          if (!userID || !password) {
            return
          }

          // The list names the rules the password breaks. The decision uses it directly:
          // state set in this handler is only visible after the next render.
          const errors = await validatePassword()
          setPasswordErrors(errors)
          if (Array.isArray(errors) && errors.length !== 0) return

          // A refused reset shows its message at the field (errorMessage above).
          const {data} = await resetUserPassword({
            variables: {
              id: userID,
              password: password,
            },
          }).catch(() => ({data: undefined}))
          if (data?.resetUserPassword) {
            toaster.push(
              <Notification
                closable
                type="success"
                header={t('userList.panels.passwordChangeSuccess')}
              ></Notification>,
              {placement: 'topEnd', duration: 2000}
            )
            onClose()
          }
        }}
      >
        {t('userList.panels.resetPassword')}
      </Button>
    </>
  )
}
